import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Home, Users, Target, Calendar, MessageSquare, FileText, Brain,
  LogOut, Menu, X, Building2, CreditCard, ClipboardList, AlertTriangle, CheckSquare, Settings, Award, Zap, Tag, BarChart2, BarChart3, ShieldAlert
} from 'lucide-react';
import { Button } from './ui/button';
import SuperAdminSupportBanner from './SuperAdminSupportBanner';
import { useWebSocketContext } from '../contexts/WebSocketContext';

// Allowlist: super_admin UTAN aktiv supportsession ser BARA dessa paths
// (plus profilen i botten). Allt annat innehåller per-org-data och döljs.
const SUPER_ADMIN_NO_SESSION_ALLOWED_PATHS = new Set([
  '/dashboard',
  '/admin/customers',
  '/admin/contracts',
  '/admin/invoices',
  '/admin/subscription-plans',
  '/admin/platform-analytics',
  '/users-admin',
  '/admin/support-sessions',
  '/settings/profile',
]);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const navigationGroups = [
  {
    label: null,
    items: [
      { name: 'Dashboard', path: '/dashboard', icon: Home },
      { name: 'Analytics', path: '/analytics', icon: BarChart2, roles: ['super_admin', 'customer_admin'] },
    ],
  },
  {
    label: 'VERKSAMHET',
    items: [
      { name: 'Kunder', path: '/clients', icon: Building2, roles: ['super_admin'] },
      { name: 'Verksamheter', path: '/verksamheter', icon: Building2, roles: ['super_admin', 'customer_admin'] },
    ],
  },
  {
    label: 'DELTAGARE',
    items: [
      { name: 'Deltagare', path: '/participants', icon: Users, roles: ['super_admin', 'customer_admin', 'employee'] },
      { name: 'Kartläggningar', path: '/assessments', icon: FileText, roles: ['super_admin', 'customer_admin', 'employee'] },
      { name: 'Mål', path: '/goals', icon: Target, roles: ['super_admin', 'customer_admin', 'employee'] },
      { name: 'Mötesanteckningar', path: '/meeting-notes', icon: ClipboardList, roles: ['super_admin', 'customer_admin', 'employee'] },
      { name: 'Närvaro', path: '/attendance', icon: Calendar, roles: ['super_admin', 'customer_admin', 'employee'] },
      { name: 'Validering', path: '/ocn-validations', icon: Award, ocnRequired: true },
      { name: 'Avvikelser', path: '/deviations', icon: AlertTriangle, roles: ['super_admin', 'customer_admin', 'employee'] },
    ],
  },
  {
    label: 'UPPFÖLJNING',
    items: [
      { name: 'Uppföljningar', path: '/follow-ups', icon: CheckSquare, roles: ['super_admin', 'customer_admin', 'employee', 'read_only_participant'] },
      { name: 'Meddelanden', path: '/messages', icon: MessageSquare, roles: ['super_admin', 'customer_admin', 'employee', 'read_only_participant'] },
      { name: 'Rapporter', path: '/reports', icon: FileText, roles: ['super_admin', 'customer_admin', 'read_only_assigner', 'read_only_participant'] },
    ],
  },
  {
    label: 'KONFIGURATION',
    items: [
      { name: 'Taggar', path: '/tags', icon: Tag, roles: ['super_admin', 'customer_admin', 'employee'] },
      { name: 'AI-planer', path: '/ai-plans', icon: Brain, roles: ['super_admin', 'customer_admin'] },
      { name: 'AI-användning', path: '/admin/ai-usage', icon: Zap, roles: ['super_admin'] },
    ],
  },
  {
    label: 'ADMINISTRATION',
    items: [
      { name: 'Kundorganisationer', path: '/admin/customers', icon: Building2, roles: ['super_admin'] },
      { name: 'Avtal', path: '/admin/contracts', icon: FileText, roles: ['super_admin'] },
      { name: 'Fakturor', path: '/admin/invoices', icon: CreditCard, roles: ['super_admin'] },
      { name: 'Prenumerationsplaner', path: '/admin/subscription-plans', icon: CreditCard, roles: ['super_admin'] },
      { name: 'Plattform', path: '/admin/platform-analytics', icon: BarChart3, roles: ['super_admin'] },
      { name: 'Prenumeration', path: '/settings/subscription', icon: CreditCard, roles: ['customer_admin'] },
      { name: 'Fakturor', path: '/invoices', icon: FileText, roles: ['customer_admin'] },
      { name: 'Användare', path: '/users-admin', icon: Users, roles: ['super_admin', 'customer_admin'] },
      { name: 'Supportsessioner', path: '/admin/support-sessions', icon: ShieldAlert, roles: ['super_admin'] },
    ],
  },
];

const profileItem = { name: 'Min profil', path: '/settings/profile', icon: Settings };

const firstLabeledIndex = navigationGroups.findIndex(g => g.label !== null);

const DashboardLayout = ({ children }) => {
  const { user, logout, token } = useAuth();
  const { unreadMessages } = useWebSocketContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [superAdminHasActiveSession, setSuperAdminHasActiveSession] = useState(false);

  useEffect(() => {
    if (user?.role !== 'super_admin' || !token) {
      setSuperAdminHasActiveSession(false);
      return undefined;
    }
    let cancelled = false;
    const check = async () => {
      try {
        const resp = await axios.get(`${API_URL}/admin/support-sessions/my-active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setSuperAdminHasActiveSession(Array.isArray(resp.data) && resp.data.length > 0);
        }
      } catch (err) {
        if (!cancelled) setSuperAdminHasActiveSession(false);
      }
    };
    check();
    const id = setInterval(check, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.role, token]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filterItem = (item) => {
    if (user?.role === 'read_only_participant') {
      return item.path === '/dashboard' || item.path === '/messages';
    }
    // GDPR break-glass: super_admin utan session ser bara administrations-paths.
    if (user?.role === 'super_admin' && !superAdminHasActiveSession) {
      return SUPER_ADMIN_NO_SESSION_ALLOWED_PATHS.has(item.path);
    }
    if (item.ocnRequired) {
      if (user?.role === 'read_only_assigner') return false;
      if (user?.role === 'employee' && !user?.ocn_access) return false;
      return true;
    }
    return !item.roles || item.roles.includes(user?.role);
  };

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    const displayName =
      user?.role === 'read_only_participant' && item.path === '/dashboard'
        ? 'Min sida'
        : item.name;
    return (
      <Link
        key={item.path}
        to={item.path}
        data-testid={`nav-${item.path.slice(1).replace(/\//g, '-')}`}
        className={`sidebar-nav-item flex items-center gap-3 px-4 py-3 rounded-md transition-all${isActive ? ' active' : ''}`}
        onClick={() => setSidebarOpen(false)}
      >
        <Icon
          size={20}
          style={{ color: isActive ? 'var(--accent-color)' : 'var(--sidebar-text-muted)', flexShrink: 0 }}
        />
        <span style={{ color: isActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-text)' }}>
          {displayName}
        </span>
        {item.path === '/messages' && unreadMessages > 0 && (
          <span
            data-testid="sidebar-unread-badge"
            className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-primary text-[#333] text-[11px] font-black flex items-center justify-center"
          >
            {unreadMessages > 9 ? '9+' : unreadMessages}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--content-bg)' }}>
      {/* Mobile header */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 z-50 p-4 flex items-center justify-between"
        style={{ backgroundColor: 'var(--sidebar-bg)', borderBottom: '1px solid var(--sidebar-border)' }}
      >
        <img
          src="/Logos/logo-dark.png"
          alt="AIUS Control"
          style={{ height: '28px', width: 'auto' }}
        />
        <Button
          data-testid="mobile-menu-btn"
          variant="ghost"
          size="icon"
          style={{ color: 'var(--sidebar-text)' }}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X /> : <Menu />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-screen w-64 p-6 flex flex-col gap-2 z-40
          transition-transform duration-300 overflow-y-auto
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        style={{ backgroundColor: 'var(--sidebar-bg)', borderRight: '1px solid var(--sidebar-border)' }}
        data-testid="sidebar"
      >
        <div className="mb-8">
          <img
            src="/Logos/logo-dark.png"
            alt="AIUS Control"
            style={{ height: '28px', width: 'auto' }}
          />
        </div>

        <nav className="flex-1">
          {navigationGroups.map((group, groupIndex) => {
            const visibleItems = group.items.filter(filterItem);
            if (visibleItems.length === 0) return null;
            return (
              <div key={groupIndex}>
                {group.label && (
                  <div style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    color: 'var(--sidebar-text-muted)',
                    textTransform: 'uppercase',
                    padding: groupIndex === firstLabeledIndex ? '0 16px 4px 16px' : '16px 16px 4px 16px',
                  }}>
                    {group.label}
                  </div>
                )}
                {visibleItems.map(renderNavItem)}
              </div>
            );
          })}
          {renderNavItem(profileItem)}
        </nav>

        <div className="pt-4 mt-4" style={{ borderTop: '1px solid var(--sidebar-border)' }}>
          <div className="px-4 py-2 text-sm">
            <p className="font-semibold" style={{ color: 'var(--sidebar-text)' }}>{user?.name}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--sidebar-text-muted)' }}>{user?.email}</p>
            <p className="text-xs mt-1 capitalize" style={{ color: 'var(--sidebar-text-muted)' }}>{user?.role?.replace('_', ' ')}</p>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start mt-2 hover:bg-gray-100"
            style={{ color: 'var(--sidebar-text-muted)' }}
          >
            <LogOut size={20} className="mr-3" />
            Logga ut
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64 min-h-screen" style={{ backgroundColor: 'var(--content-bg)' }}>
        <SuperAdminSupportBanner />
        <main className="p-6 md:p-12 mt-16 lg:mt-0">
          {children}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
